'use client'

import { useState } from 'react'
import { ChefHat, Search, X, Clock, Utensils, ExternalLink } from 'lucide-react'
import api from '@/lib/platform-api'
import { formatFastApiDetail } from '@/lib/format-fastapi-detail'
import { useNotification } from '@/context/NotificationContext'
import { useRequireModule } from '@/hooks/useRequireModule'

interface Recipe {
  recipe_name?: string
  cuisine?: string
  cooking_time_mins?: number
  ingredient_count?: number
  ingredients?: string[]
  original_ingredients?: string[]
  instructions?: string
  image_url?: string
  url?: string
}

function formatInstructions(text: string): string[] {
  if (!text) return []
  const cleaned = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n{3,}/g, '\n\n')
  const stepPatterns = [/^\d+[\.\)]\s+/m, /^Step\s+\d+[\.\)]?\s*:?\s*/mi, /^\(\d+\)\s+/m]
  for (const pattern of stepPatterns) {
    if (pattern.test(cleaned)) {
      return cleaned.split(pattern).filter(s => s.trim()).map(s => s.replace(/^\d+[\.\)]\s*/, '').trim())
    }
  }
  const steps = cleaned.split(/\n{2,}/).filter(s => s.trim())
  if (steps.length > 1) return steps
  return cleaned.split('\n').filter(s => s.trim()).map(s => s.replace(/^[•\-\*]\s+/, '').replace(/^\d+[\.\)]\s+/, '').trim()).filter(Boolean)
}

type SearchMode = 'ingredient' | 'name'

export default function RecipesPage() {
  const { ready } = useRequireModule('module_recipe_search')
  const { addNotification } = useNotification()
  const [query, setQuery] = useState('')
  const [searchMode, setSearchMode] = useState<SearchMode>('ingredient')
  const [recipes, setRecipes] = useState<Recipe[]>([])
  const [searching, setSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [selected, setSelected] = useState<Recipe | null>(null)

  const handleSearch = async () => {
    if (!query.trim()) {
      addNotification({ type: 'error', title: 'Error', message: `Please enter a${searchMode === 'name' ? ' recipe name' : 'n ingredient'}` })
      return
    }
    setSearching(true); setHasSearched(true); setRecipes([])
    try {
      if (searchMode === 'name') {
        const res = await api.get('/search-recipes-by-name/', { params: { q: query.trim() } })
        if (res.data.success) {
          const normalized = (res.data.results || []).map((r: Recipe) => ({
            ...r,
            ingredients: typeof r.ingredients === 'string'
              ? (r.ingredients as string).split(',').map((s: string) => s.trim()).filter(Boolean)
              : r.ingredients,
          }))
          setRecipes(normalized)
          const count = res.data.results_count || 0
          if (count > 0) addNotification({ type: 'success', title: 'Found!', message: `Found ${count} recipes` })
          else addNotification({ type: 'info', title: 'No results', message: 'No recipes found for this name' })
        }
      } else {
        const res = await api.get('/search-recipes-by-ingredient/', { params: { q: query.trim() } })
        if (res.data.success) {
          setRecipes(res.data.recipe_names || [])
          if ((res.data.recipes_found || 0) > 0) addNotification({ type: 'success', title: 'Found!', message: `Found ${res.data.recipes_found} recipes` })
          else addNotification({ type: 'info', title: 'No results', message: 'No recipes found for this ingredient' })
        }
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string }
      addNotification({
        type: 'error',
        title: 'Error',
        message: formatFastApiDetail(e.response?.data?.detail, 'Search failed'),
      })
    } finally { setSearching(false) }
  }

  if (!ready) return null

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white mb-4 shadow-lg shadow-amber-400/30">
            <ChefHat size={28} />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent mb-2">Recipe Search</h1>
          <p className="text-slate-500 text-lg">
            {searchMode === 'name' ? 'Search by recipe name — get full instructions and prep times' : 'Find recipes by ingredient — get full instructions and prep times'}
          </p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-8">
          <div className="flex gap-3">
            <select
              value={searchMode}
              onChange={e => { setSearchMode(e.target.value as SearchMode); setQuery(''); setRecipes([]); setHasSearched(false) }}
              className="px-4 py-3.5 rounded-xl border border-slate-200 focus:border-amber-400 focus:ring-4 focus:ring-amber-400/10 outline-none text-slate-700 bg-white font-medium transition-all cursor-pointer shrink-0"
            >
              <option value="ingredient">By Ingredient</option>
              <option value="name">By Recipe Name</option>
            </select>
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-slate-200 focus:border-amber-400 focus:ring-4 focus:ring-amber-400/10 outline-none text-slate-700 placeholder-slate-400 transition-all"
                placeholder={searchMode === 'name' ? 'Enter recipe name (e.g., butter chicken, palak paneer)...' : 'Enter ingredient (e.g., chicken, turmeric, rice flour)...'}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <button onClick={handleSearch} disabled={searching}
              className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-white font-semibold shadow-md shadow-amber-400/25 hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap">
              {searching ? 'Searching…' : 'Search Recipes'}
            </button>
          </div>
        </div>

        {/* Results */}
        {hasSearched && (
          searching ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center text-slate-400">Searching for recipes…</div>
          ) : recipes.length > 0 ? (
            <div>
              <p className="text-sm text-slate-500 mb-4">Found <strong className="text-slate-700">{recipes.length}</strong> recipes {searchMode === 'name' ? 'matching' : 'containing'} &ldquo;{query}&rdquo;</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {recipes.map((recipe, i) => (
                  <div key={i}
                    className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all cursor-pointer"
                    onClick={() => setSelected(recipe)}>
                    {recipe.image_url && (
                      <div className="h-44 overflow-hidden bg-slate-100">
                        <img src={recipe.image_url} alt={recipe.recipe_name} className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                      </div>
                    )}
                    <div className="p-5">
                      <h3 className="font-semibold text-slate-800 text-lg mb-3 leading-snug">{recipe.recipe_name || 'Unnamed Recipe'}</h3>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {recipe.cuisine && <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-blue-50 text-blue-600">{recipe.cuisine}</span>}
                        {recipe.cooking_time_mins && <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-600 flex items-center gap-1"><Clock size={10} />{recipe.cooking_time_mins} min</span>}
                        {recipe.ingredient_count && <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-purple-50 text-purple-600 flex items-center gap-1"><Utensils size={10} />{recipe.ingredient_count} ingredients</span>}
                      </div>
                      {recipe.ingredients && recipe.ingredients.length > 0 && (
                        <p className="text-xs text-slate-400 line-clamp-2">
                          {recipe.ingredients.slice(0, 5).join(', ')}{recipe.ingredients.length > 5 && ` +${recipe.ingredients.length - 5} more`}
                        </p>
                      )}
                      <button className="mt-4 w-full py-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-semibold hover:shadow-md hover:shadow-amber-400/25 transition-all">
                        View Full Recipe →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
              <ChefHat size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No recipes found</p>
              <p className="text-slate-400 text-sm mt-1">Try a different {searchMode === 'name' ? 'recipe name' : 'ingredient'}</p>
            </div>
          )
        )}
      </div>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-auto" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors z-10">
              <X size={18} />
            </button>
            {selected.image_url && (
              <div className="h-64 overflow-hidden rounded-t-3xl bg-slate-100">
                <img src={selected.image_url} alt={selected.recipe_name} className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
              </div>
            )}
            <div className="p-8">
              <h1 className="text-3xl font-bold text-slate-800 mb-4">{selected.recipe_name || 'Unnamed Recipe'}</h1>
              <div className="flex flex-wrap gap-2 mb-6">
                {selected.cuisine && <span className="px-3 py-1.5 rounded-xl bg-blue-50 text-blue-600 text-sm font-semibold">{selected.cuisine}</span>}
                {selected.cooking_time_mins && <span className="px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-600 text-sm font-semibold flex items-center gap-1"><Clock size={12} /> {selected.cooking_time_mins} minutes</span>}
                {selected.ingredient_count && <span className="px-3 py-1.5 rounded-xl bg-purple-50 text-purple-600 text-sm font-semibold flex items-center gap-1"><Utensils size={12} /> {selected.ingredient_count} ingredients</span>}
              </div>
              {selected.original_ingredients && selected.original_ingredients.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-100">Ingredients</h2>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <ul className="list-disc pl-5 space-y-1.5 text-slate-600 text-sm">
                      {selected.original_ingredients.map((ing, i) => <li key={i}>{ing}</li>)}
                    </ul>
                  </div>
                </div>
              )}
              {selected.instructions && (
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-100">Instructions</h2>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <ol className="list-decimal pl-5 space-y-3 text-slate-600 text-sm">
                      {formatInstructions(selected.instructions).map((step, i) => <li key={i}>{step}</li>)}
                    </ol>
                  </div>
                </div>
              )}
              {selected.url && (
                <a href={selected.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-blue-500 hover:text-blue-700 text-sm font-semibold mt-2 hover:underline">
                  <ExternalLink size={14} /> View Original Source
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
